import React, {
    Fragment,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { Transition } from "@headlessui/react";
import { useDropzone } from "react-dropzone";
import { reduce } from "lodash";
import { apiPost, oauthGet, apiPut } from "helpers/Request";
import { useAuth } from "hooks/Auth";
import { isTrueOrUndefined, nonce, packClasses } from "Utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { CubeIcon, XCircleIcon } from "@heroicons/react/outline";
import PrimaryButton from "components/Buttons/PrimaryButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import { transform as contentTransformer } from "schemas/Content";
import {
    ContainerObjectSchema,
    prepare as containerPreparer,
} from "schemas/Container";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import PostUploadCart from "./PostUploadCart";
import { SQL_DATE_TIME_FORMAT, fromNow } from "helpers/Date";
import { getLocalStorageItem } from "helpers/LocalStorage";

function sizeFromBytes(bytes) {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 Byte";
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
    return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
}

export default function Dropzone(props) {
    const auth = useAuth();

    const { settings } = useSelector((state) => ({
        settings: state.contentFlow?.settings,
    }));

    const [files, setFiles] = useState([]);
    const [totalSize, setTotalSize] = useState(0);
    const [previews, setPreviews] = useState([]);
    const [open, setOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploaded, setUploaded] = useState([]);
    const [progress, setProgress] = useState(0);

    const { showDropzone, isInternalDrag } = props;
    // const inputRef = useRef();
    const checkRef = useRef();
    const pubRef = useRef({
        checked: true,
    });

    const incrementProgress = () => {
        setProgress((progress) => progress + 1);
    };

    const fileSizeValidator = (file) => {
        if (file.size > settings.env.file_upload_size) {
            const largeFileMessage = `That file is too large. The maximum single file size is ${sizeFromBytes(
                settings.env.file_upload_size
            )}`;
            toast.error(largeFileMessage);
            return {
                code: "file-too-large",
                message: largeFileMessage,
            };
        } else if (file.size + totalSize > settings.env.total_upload_size) {
            const uploadMessage = `That file would put you over the total upload limit of ${sizeFromBytes(
                settings.env.total_upload_size
            )}`;
            toast.error(uploadMessage);
            return {
                code: "upload-too-large",
                message: uploadMessage,
            };
        }

        return null;
    };
    const { getRootProps, getInputProps } = useDropzone({
        validator: fileSizeValidator,
        onDrop: (acceptedFiles) => {
            const newFiles = acceptedFiles
                .map((file) =>
                    Object.assign(file, {
                        preview:
                            file.type.indexOf("image") !== -1
                                ? URL.createObjectURL(file)
                                : false,
                    })
                )
                .concat(files);

            // the reducer keeps duplicate files out of the stack
            setFiles(
                reduce(
                    newFiles,
                    (accumulator, file) => {
                        if (
                            accumulator.find(({ name }) => name === file.name)
                        ) {
                            return accumulator;
                        }
                        accumulator.push(file);
                        return accumulator;
                    },
                    []
                )
            );
        },
    });

    const removeFile = useCallback(
        (file) => {
            setFiles(files.filter((f) => f.name !== file.name));
        },
        [files]
    );

    const requestClose = () => {
        // files.forEach((file) => URL.revokeObjectURL(file.preview));
        setOpen(false);
        setFiles([]);
        setPreviews([]);
        props.onRequestClose();
    };

    // const uploadFiles = async () => {
    //     const batch = nonce();
    //     setUploading(true);

    //     let collection = null;

    //     const allUploads = [];
    //     const promises = files.map((file) => {
    //         const formData = new FormData();

    //         formData.append("slug", file.name);
    //         formData.append("title", file.name);
    //         formData.append("batch", batch);
    //         formData.append("file", file, file.name);
    //         if (collection) {
    //             formData.append("collections[]", collection.uuid);
    //         }

    //         return apiPost("/v4/content", auth.publication, formData).then(
    //             (resp) => {
    //                 allUploads.push(contentTransformer(resp).uuid);
    //             }
    //         );
    //     });

    //     Promise.all(promises)
    //         .then(() => {
    //             setUploading(false);
    //             toast.success("Files uploaded successfully");
    //             setTimeout(requestClose, 100);
    //         })
    //         .then(() => {
    //             if (!checkRef.current.checked) {
    //                 return allUploads;
    //             }

    //             // if there's a new gallery, create it here:
    //             const data = containerPreparer(
    //                 Object.assign(
    //                     {},
    //                     ContainerObjectSchema,
    //                     {
    //                         content: allUploads,
    //                         title: "Upload Gallery (auto-generated)",
    //                         type: "gallery",
    //                     },
    //                     {
    //                         modified_at: undefined,
    //                     }
    //                 )
    //             );
    //             return apiPost("/v4/container", auth.publication, data).then(
    //                 () => setUploaded(allUploads)
    //             );
    //         })
    //         .then((allUploads) => {
    //             setUploaded(allUploads);
    //         })
    //         .catch((err) => {
    //             toast.error("Unable to upload " + err.message);
    //             setUploading(false);
    //         });
    // };

    const uploadFilesV5 = async () => {
        const batch = nonce();
        setUploading(true);

        let collection = null;

        const federatedLoginToken = await oauthGet(
            "/api/v2/me/federated-token",
            { activity: "upload" }
        );
        if (!federatedLoginToken || !federatedLoginToken.token) {
            toast.error("Unable to get federated token");
            return;
        }

        const promises = files.map((file) => {
            const formData = new FormData();

            formData.append("slug", file.name);
            formData.append("title", file.name);
            formData.append("batch", batch);
            formData.append("file", file, file.name);
            if (collection) {
                formData.append("collections[]", collection.uuid);
            }

            return apiPost(
                "/v5/import",
                auth.publication,
                formData,
                true,
                () => federatedLoginToken.token
            )
                .then((resp) => {
                    incrementProgress();
                    return resp;
                })
                .catch((err) => {
                    Promise.reject(err.message);
                });
        });
        // promiseSerial(promises)
        Promise.allSettled(promises)
            .then((values) => {
                setUploading(false);
                const doGallery = checkRef.current.checked;
                const doPublish = pubRef.current.checked;

                toast.success("Files uploaded successfully");
                setTimeout(requestClose, 100);
                setProgress(0);

                const allUploads = [];

                for (let i = 0; i < values.length; i++) {
                    const value = values[i];

                    if (value.status === "rejected") {
                        toast.error("Unable to upload " + value.reason);
                    }
                    allUploads.push(contentTransformer(value.value).uuid);
                }

                return {
                    uploads: allUploads,
                    gallery: doGallery,
                    publish: doPublish,
                };
            })
            .then(async ({ uploads, gallery, publish }) => {
                if (!gallery) {
                    return { uploads, gallery, publish };
                }

                // if there's a new gallery, create it here:
                const postData = containerPreparer(
                    Object.assign(
                        {},
                        ContainerObjectSchema,
                        {
                            content: uploads,
                            title: "Upload Gallery (auto-generated)",
                            type: "gallery",
                        },
                        {
                            modified_at: undefined,
                            published_at: undefined,
                            // published_at: publish
                            //     ? fromNow()
                            //           .toUTC()
                            //           .toFormat(SQL_DATE_TIME_FORMAT)
                            //     : undefined,
                        }
                    )
                );
                await apiPost("/v4/container", auth.publication, postData);

                return { uploads, gallery, publish };
            })
            .then(async ({ uploads, publish, ...rest }) => {
                if (!publish) {
                    return { uploads, publish, ...rest };
                }

                const postData = {
                    forced: true,
                    published_at: fromNow()
                        .toUTC()
                        .toFormat(SQL_DATE_TIME_FORMAT),
                };

                const promises = uploads.map((uuid) =>
                    apiPut(`/v4/content/${uuid}`, auth.publication, postData)
                );

                await Promise.allSettled(promises);
                return { uploads, publish, ...rest };
            })
            .then(({ uploads }) => {
                setUploaded(uploads);
            });
    };

    useEffect(() => {
        setPreviews(
            files.map((file) => (
                <div
                    key={file.name}
                    className="bg-gray-50 p-2 shadow-sm hover:shadow-md"
                >
                    <button
                        onClick={() => removeFile(file)}
                        className="ml-auto block"
                    >
                        <XCircleIcon className="h-4 w-4 text-gray-800" />
                    </button>
                    {file.type.indexOf("image") > -1 ? (
                        <img
                            src={file.preview}
                            className="mx-auto h-20"
                            alt={`Upload preview for ${file.name}`}
                            onLoad={() => URL.revokeObjectURL(file.preview)}
                        />
                    ) : (
                        <CubeIcon className="mx-auto h-20 w-20 text-gray-500" />
                    )}
                    <div className="mt-2 text-center text-gray-800">
                        <span className=" text-xs">
                            {file.name} - {sizeFromBytes(file.size)}
                        </span>
                    </div>
                </div>
            ))
        );
    }, [files, removeFile]);

    useEffect(() => {
        setTotalSize(
            files.reduce((accumulator, file) => {
                return accumulator + file.size;
            }, 0)
        );
    }, [files]);

    useEffect(() => {
        if (showDropzone && !isInternalDrag) {
            setOpen(true);
        }
    }, [showDropzone, isInternalDrag]);

    return (
        <Fragment>
            <PostUploadCart uploaded={uploaded} />
            <Transition
                show={open}
                enter="transition-opacity duration-75"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed top-0 left-0 z-20 h-full w-full bg-gray-500 bg-opacity-50">
                    <div className="absolute inset-0 m-10 mb-0 flex w-auto flex-col rounded bg-white shadow md:m-40 lg:m-52">
                        <div className="flex-grow overflow-y-auto">
                            <div {...getRootProps({ className: "dropzone" })}>
                                <input {...getInputProps()} />
                                <div
                                    className={packClasses(
                                        "m-10 mb-0 rounded border-2 border-dashed border-gray-400 text-center tracking-tighter transition-all ",
                                        previews.length
                                            ? "p-2 text-gray-500"
                                            : "p-10 text-gray-800"
                                    )}
                                >
                                    {!previews.length && (
                                        <FontAwesomeIcon
                                            className="mx-auto mb-2 block h-20 w-20 text-gray-600"
                                            icon={solid("cloud-arrow-up")}
                                        />
                                    )}
                                    Drag 'n' drop some files here, or click to
                                    select files
                                </div>
                            </div>
                            {/*<div className="grid grid-cols-2 gap-4 py-4 px-10">
                                <div>
                                    <Label>Add uploads to new collection</Label>
                                    <input
                                        type="text"
                                        ref={inputRef}
                                        className="block w-full rounded-md border-gray-300 py-2 px-4 shadow-sm focus:border-green-500 focus:ring-green-500 disabled:bg-white sm:text-sm"
                                    />
                                </div>
                            </div>*/}
                            <div className="m-10 grid flex-grow grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
                                {previews}
                            </div>
                        </div>
                        <div className="mb-2 px-4">
                            <div className="w-100 relative h-7 rounded-sm bg-gray-100 text-right shadow-inner">
                                {uploading ? (
                                    <Fragment>
                                        <div
                                            className="absolute top-0 left-0 h-full animate-pulse rounded-sm bg-green-600"
                                            style={{
                                                width:
                                                    (progress / files.length) *
                                                        100 +
                                                    "%",
                                            }}
                                        ></div>
                                        <span className="pr-2 text-xs text-gray-500 text-white">
                                            Uploading {sizeFromBytes(totalSize)}
                                        </span>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <div className="absolute top-0 left-0 h-full rounded-sm bg-green-600"></div>
                                        <span className="pr-2 text-xs text-gray-500">
                                            Max upload{" "}
                                            {sizeFromBytes(
                                                settings.env.total_upload_size
                                            )}
                                        </span>
                                    </Fragment>
                                )}
                            </div>
                        </div>
                        <div className="rounded-bl-lg rounded-br-lg bg-gray-100 px-4 py-2">
                            <div className="items-center justify-between space-y-2 space-x-2 md:flex md:space-y-0">
                                <div className="lg:mr-5">
                                    <label
                                        htmlFor="_dropzone-create-gallery"
                                        className=" text-center md:text-left"
                                    >
                                        <input
                                            type="checkbox"
                                            className="appearance-none rounded text-green-700 focus:ring-green-700"
                                            id="_dropzone-create-gallery"
                                            ref={checkRef}
                                        />
                                        <span className="pl-1 text-sm text-gray-800">
                                            Group uploads into new gallery
                                        </span>
                                    </label>
                                </div>
                                <div className="md:flex-grow">
                                    <label
                                        htmlFor="_dropzone-publish-media"
                                        className=" text-center md:text-left"
                                    >
                                        <input
                                            type="checkbox"
                                            className="appearance-none rounded text-green-700 focus:ring-green-700"
                                            id="_dropzone-publish-media"
                                            defaultChecked={isTrueOrUndefined(
                                                getLocalStorageItem(
                                                    "local_autoPublishUploads"
                                                )
                                            )}
                                            ref={pubRef}
                                        />
                                        <span className="pl-1 text-sm text-gray-800">
                                            Publish uploads
                                        </span>
                                    </label>
                                </div>
                                {/*<div className="md:pr-10">
                                    <SecondaryButton
                                        onClick={uploadFilesV5}
                                        label={
                                            <BeakerIcon className="h-5 w-5" />
                                        }
                                        className="w-full md:w-auto"
                                    />
                                    </div>*/}
                                <div>
                                    <SecondaryButton
                                        onClick={requestClose}
                                        label="Cancel"
                                        className="w-full md:w-auto"
                                    />
                                </div>
                                <div>
                                    <PrimaryButton
                                        onClick={uploadFilesV5}
                                        label="Upload Files"
                                        blocking={uploading}
                                        className="w-full md:w-auto"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </Fragment>
    );
}
